import { format } from 'date-fns';
import {
  CalendarIcon,
  DollarSignIcon,
  FileTextIcon,
  ReceiptIcon,
} from 'lucide-react';

import BlobPreview from '@/components/documents-v2/components/inspector/BlobPreview';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useMatter } from '@/hooks/useMatter';
import { cn } from '@/lib/utils';
import { blobsApiClient } from '@/services/api/blobService';
import type {
  TreatmentRecordRequestInvoiceSchema,
  TreatmentRecordRequestSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

const InvoiceCard = ({
  invoice,
  matterId,
}: {
  invoice: TreatmentRecordRequestInvoiceSchema;
  matterId: string;
}) => {
  const { data: blob, isLoading: isBlobLoading } =
    blobsApiClient.useGetBlobDetailQuery(
      {
        matterId,
        blobId: invoice.invoice_file_id || '',
      },
      {
        skip: !invoice.invoice_file_id,
      },
    );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const isPaid = !!invoice.date_paid;

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <ReceiptIcon className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">
                {invoice.invoice_number || 'Invoice'}
              </CardTitle>
              <div className="text-sm text-muted-foreground mt-1">
                Received{' '}
                {format(new Date(invoice.date_received), 'MMM d, yyyy')}
              </div>
            </div>
          </div>
          <Badge
            variant={isPaid ? 'default' : 'secondary'}
            className={cn(
              isPaid
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800',
            )}
          >
            {isPaid ? 'Paid' : 'Pending'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <DollarSignIcon className="h-3.5 w-3.5" />
              <span>Amount</span>
            </div>
            <div className="font-semibold text-lg">
              {formatCurrency(invoice.amount)}
            </div>
          </div>
          {isPaid && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <CalendarIcon className="h-3.5 w-3.5" />
                <span>Paid Date</span>
              </div>
              <div className="font-medium">
                {format(new Date(invoice.date_paid || ''), 'MMM d, yyyy')}
              </div>
            </div>
          )}
        </div>

        {invoice.invoice_file_id && (
          <div className="border rounded-lg overflow-hidden bg-muted/30">
            {isBlobLoading ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : blob ? (
              <div className="h-64">
                <BlobPreview file={blob} />
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                <FileTextIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                <p>Invoice document not available</p>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (blob?.download_url) {
                window.open(blob.download_url, '_blank');
              }
            }}
            disabled={!blob?.download_url}
          >
            View Full Document
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const InvoiceListSkeleton = () => (
  <div className="space-y-4">
    {[1, 2, 3].map((i) => (
      <Card key={i}>
        <CardHeader>
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-24 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const EmptyState = () => (
  <Card className="border-dashed">
    <CardContent className="flex flex-col items-center justify-center py-12">
      <ReceiptIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
      <h3 className="font-semibold text-lg mb-1">No Invoices Found</h3>
      <p className="text-sm text-muted-foreground text-center max-w-sm">
        No invoices have been submitted for this records request yet.
      </p>
    </CardContent>
  </Card>
);

export const InvoiceList = ({
  request,
}: {
  request: TreatmentRecordRequestSchema;
}) => {
  const { matterId } = useMatter();

  const { data: invoices, isLoading } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsInvoicesListQuery(
      {
        matterId,
        providerId: request.provider_id,
        requestId: request.id,
      },
    );

  if (isLoading) {
    return <InvoiceListSkeleton />;
  }

  if (!invoices || invoices.length === 0) {
    return <EmptyState />;
  }

  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const paidInvoices = invoices.filter((inv) => inv.date_paid);
  const totalPaid = paidInvoices.reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Total Invoices</div>
            <div className="text-2xl font-bold">{invoices.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Total Amount</div>
            <div className="text-2xl font-bold">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(totalAmount)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Amount Paid</div>
            <div className="text-2xl font-bold">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(totalPaid)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        {invoices.map((invoice) => (
          <InvoiceCard key={invoice.id} invoice={invoice} matterId={matterId} />
        ))}
      </div>
    </div>
  );
};
